/**
 * Pure listing logic shared by the media library and the picker (spec 003):
 * type kinds and counts, search, sort, and folder-tree checks.
 */
import type { MediaItem } from '../../@types/Media';

export type MediaKind = 'image' | 'video' | 'audio' | 'other';
export type KindFilter = 'all' | MediaKind;
export type SortBy = 'name' | 'type' | 'newest';
export type SortDirection = 'asc' | 'desc';

/** Accepted by the Service's upload route (`POST /media`). */
export const ACCEPTED_UPLOAD = 'image/*,audio/*,video/*';
export const ACCEPTED_UPLOAD_LABEL = 'images, audio and video';

export function kindOf(type: string | undefined): MediaKind {
  if (!type) return 'other';
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  return 'other';
}

/** Whether an item has a playing length to show (spec 041): audio and video. */
export const hasMediaLength = (type: string | undefined) => {
  const kind = kindOf(type);
  return kind === 'audio' || kind === 'video';
};

/**
 * A media length for the library, the pickers and the line panel (spec 041):
 * under a minute `6.6 s` (one decimal), from a minute `1:05` (`m:ss`);
 * `length unknown` when absent or not a positive number.
 */
export function formatMediaLength(durationMs?: number): string {
  if (typeof durationMs !== 'number' || !Number.isFinite(durationMs) || durationMs <= 0) return 'length unknown';
  const tenths = Math.round(durationMs / 100);
  if (tenths < 600) return `${(tenths / 10).toFixed(1)} s`;
  const total = Math.round(durationMs / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

export const isAcceptedUpload = (file: { type: string }) => kindOf(file.type) !== 'other';

export const FILTERS: { key: KindFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'image', label: 'Images' },
  { key: 'video', label: 'Videos' },
  { key: 'audio', label: 'Audio' },
  { key: 'other', label: 'Other' },
];

/** Counts per filter over the given items, before any filtering. */
export function countByKind(items: MediaItem[]): Record<KindFilter, number> {
  const counts: Record<KindFilter, number> = { all: items.length, image: 0, video: 0, audio: 0, other: 0 };
  for (const item of items) counts[kindOf(item.type)] += 1;
  return counts;
}

export const folderOf = (item: { folderId?: number | null }) => item.folderId ?? null;

export function filterAndSortMedia(
  items: MediaItem[],
  opts: { search: string; kind: KindFilter; sortBy: SortBy; direction: SortDirection }
): MediaItem[] {
  const q = opts.search.trim().toLowerCase();
  const result = items.filter(
    (item) =>
      (!q || item.name.toLowerCase().includes(q)) && (opts.kind === 'all' || kindOf(item.type) === opts.kind)
  );
  const dir = opts.direction === 'asc' ? 1 : -1;
  result.sort((a, b) => {
    switch (opts.sortBy) {
      case 'newest':
        return b.id - a.id;
      case 'type':
        return dir * (a.type.localeCompare(b.type) || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
      case 'name':
      default:
        return dir * a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true });
    }
  });
  return result;
}

export function sortFoldersByName<T extends { name: string }>(folders: T[]): T[] {
  return [...folders].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }));
}

interface TreeFolder {
  id: number;
  parentId?: number | null;
}

/** True when `candidateId` is `folderId` itself or anywhere below it. */
export function isSelfOrDescendant(folders: TreeFolder[], folderId: number, candidateId: number | null): boolean {
  let current: number | null = candidateId;
  const seen = new Set<number>();
  while (current !== null && !seen.has(current)) {
    if (current === folderId) return true;
    seen.add(current);
    current = folders.find((f) => f.id === current)?.parentId ?? null;
  }
  return false;
}

/** Everything inside a folder, recursively: the counts a delete confirmation shows. */
export function folderContents(
  folders: TreeFolder[],
  media: { folderId?: number | null }[],
  folderId: number
): { folders: number; media: number } {
  const inside = folders.filter((f) => f.id !== folderId && isSelfOrDescendant(folders, folderId, f.id));
  const ids = new Set([folderId, ...inside.map((f) => f.id)]);
  return { folders: inside.length, media: media.filter((m) => ids.has(folderOf(m) as number)).length };
}

/** Breadcrumb trail from the root's first child down to `folderId`. */
export function folderPath<T extends TreeFolder>(folders: T[], folderId: number | null): T[] {
  const trail: T[] = [];
  const seen = new Set<number>();
  let current = folders.find((f) => f.id === folderId);
  while (current && !seen.has(current.id)) {
    trail.unshift(current);
    seen.add(current.id);
    const parentId = current.parentId ?? null;
    current = parentId === null ? undefined : folders.find((f) => f.id === parentId);
  }
  return trail;
}

/** `hero` + `png` → `hero.png`; a name that already ends in the extension is kept. */
export function downloadName(item: Pick<MediaItem, 'name' | 'extension'>): string {
  const ext = (item.extension || '').replace(/^\./, '');
  if (!ext) return item.name;
  return item.name.toLowerCase().endsWith(`.${ext.toLowerCase()}`) ? item.name : `${item.name}.${ext}`;
}

const SORT_KEY = 'mediaListSortBy';
const DIR_KEY = 'mediaListSortDirection';

export function loadSort(): { sortBy: SortBy; direction: SortDirection } {
  let sortBy: SortBy = 'name';
  let direction: SortDirection = 'asc';
  try {
    const saved = localStorage.getItem(SORT_KEY);
    if (saved === 'name' || saved === 'type' || saved === 'newest') sortBy = saved;
    else if (saved === 'date') sortBy = 'newest';
    if (localStorage.getItem(DIR_KEY) === 'desc') direction = 'desc';
  } catch {
    // storage unavailable: defaults
  }
  return { sortBy, direction };
}

export function saveSort(sortBy: SortBy, direction: SortDirection) {
  try {
    localStorage.setItem(SORT_KEY, sortBy);
    localStorage.setItem(DIR_KEY, direction);
  } catch {
    // storage unavailable: not remembered
  }
}
